// SEO Configuration and Utilities
export interface SEOConfig {
  title: string;
  description: string;
  keywords: string[];
  ogImage?: string;
  canonical?: string;
  structuredData?: object;
}

export const defaultSEO: SEOConfig = {
  title: "Sharon Wang | AI Engineer | 3D Portfolio",
  description:
    "Sharon Wang - AI Engineer specializing in Documentation Engineering and Developer Experience. Works with TypeScript, Angular, C#, .NET, GitHub Actions, and cloud technologies. Currently at Micron Technology. Interactive 3D personal website.",
  keywords: [
    "Sharon Wang",
    "AI Engineer",
    "Documentation Engineering",
    "Developer Experience",
    "Full-Stack Developer",
    "TypeScript",
    "Angular",
    "C#",
    ".NET",
    "GitHub Actions",
    "Azure",
    "3D Portfolio",
    "Computer Science",
    "Purdue University",
    "Micron Technology",
    "CATME",
    "Web Development",
    "Three.js",
  ],
  canonical: "https://www.sharonwang.me",
};

export const seoByLanguage = {
  en: {
    ...defaultSEO,
    title: "Sharon Wang | AI Engineer | 3D Portfolio",
    description:
      "Sharon Wang - AI Engineer specializing in Documentation engineering and Developer Experience. Works with TypeScript, C#, Angular, .NET, GitHub Actions, and cloud technologies. Experience at Micron Technology and CATME. Interactive 3D portfolio showcasing professional projects and skills.",
    canonical: "https://www.sharonwang.me",
  },
  zh: {
    ...defaultSEO,
    title: "Sharon Wang | AI工程師 | 3D網站",
    description:
      "Sharon Wang - 專精於TypeScript、C#、Angular、.NET、GitHub Actions和雲端技術的AI工程師。現任職於美光科技。互動式3D個人網站。",
    canonical: "https://www.sharonwang.me/zh",
    keywords: [
      "Sharon Wang",
      "AI工程師",
      "全端開發",
      "文件工程",
      "開發者體驗",
      "TypeScript",
      "Angular",
      "C#",
      ".NET",
      "GitHub Actions",
      "雲端運算",
      "Azure",
      "3D作品集",
      "電腦科學",
      "普渡大學",
      "美光科技",
      "網頁開發",
      "Three.js",
    ],
  },
};

// Get current language from URL
export const getCurrentLanguageFromURL = (): "en" | "zh" => {
  const path = window.location.pathname;
  return path.startsWith("/zh") ? "zh" : "en";
};

// Utility function to update document head
export const updateSEO = (
  config: Partial<SEOConfig>,
  language?: "en" | "zh"
) => {
  const currentLang = language || getCurrentLanguageFromURL();
  const seoConfig = { ...seoByLanguage[currentLang], ...config };

  // Update title
  if (seoConfig.title) {
    document.title = seoConfig.title;
    updateMetaTag("og:title", seoConfig.title);
    updateMetaTag("twitter:title", seoConfig.title);
  }

  // Update description
  if (seoConfig.description) {
    updateMetaTag("description", seoConfig.description);
    updateMetaTag("og:description", seoConfig.description);
    updateMetaTag("twitter:description", seoConfig.description);
  }

  // Update keywords
  if (seoConfig.keywords) {
    updateMetaTag("keywords", seoConfig.keywords.join(", "));
  }

  // Update canonical URL
  if (seoConfig.canonical) {
    updateLinkTag("canonical", seoConfig.canonical);
    updateMetaTag("og:url", seoConfig.canonical);
  }
};

const updateMetaTag = (name: string, content: string) => {
  let element =
    document.querySelector(`meta[name="${name}"]`) ||
    document.querySelector(`meta[property="${name}"]`);

  if (!element) {
    element = document.createElement("meta");
    if (name.startsWith("og:") || name.startsWith("twitter:")) {
      element.setAttribute("property", name);
    } else {
      element.setAttribute("name", name);
    }
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

// Structured data for different sections
export const getPersonStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Sharon Wang",
  alternateName: "Sharon Wang",
  jobTitle: "AI Engineer",
  description:
    "AI Engineer specializing in Documentation Engineering and Developer Experience. Works with TypeScript, C#, Angular, .NET, and GitHub Actions at Micron Technology.",
  url: "https://www.sharonwang.me",
  sameAs: [
    "https://linkedin.com/in/shihyin-sharon-wang",
    "https://github.com/sharonwang554",
  ],
  alumniOf: {
    "@type": "CollegeOrUniversity",
    name: "Purdue University",
    address: {
      "@type": "PostalAddress",
      addressLocality: "West Lafayette",
      addressRegion: "IN",
      addressCountry: "USA",
    },
  },
  worksFor: {
    "@type": "Organization",
    name: "Micron Technology",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Taichung",
      addressCountry: "Taiwan",
    },
  },
  knowsAbout: [
    "TypeScript",
    "C#",
    "Angular",
    ".NET",
    "GitHub Actions",
    "Azure",
    "Docker",
    "Three.js",
    "Kubernetes",
    "Openshift",
    "Python",
    "Full-Stack Development",
    "Software Engineering",
    "Cloud Computing",
    "Web Development",
    "Machine Learning",
  ],
  nationality: "Taiwan",
  knowsLanguage: ["English", "Chinese (Traditional)", "Chinese (Mandarin)"],
});

export const getWebsiteStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Sharon Wang Personal Website",
  alternateName: "Sharon Wang的個人網站",
  url: "https://www.sharonwang.me",
  description:
    "Interactive 3D portfolio showcasing Sharon Wang's experience as an AI Engineer",
  author: {
    "@type": "Person",
    name: "Sharon Wang",
  },
  inLanguage: ["en", "zh-TW"],
  isAccessibleForFree: true,
});
